import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const links = [
    { label: "Work", id: "work" },
    { label: "About", id: "about" },
    { label: "Contact", id: "contact" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const lenis = window.__lenis;
        if (open) lenis?.stop();
        else lenis?.start();
    }, [open]);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 150 : 0);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "border-b border-white/5 bg-ink/80 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-end px-4 sm:px-6 lg:px-8">
                <div className="hidden items-center gap-9 sm:flex">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id.toLowerCase()}`}
                            onClick={() => go(l.id)}
                            className="group relative text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-slate-50"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-flame transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-slate-300 transition-colors duration-300 hover:border-flame/50 hover:text-white sm:hidden"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="border-t border-white/5 bg-ink/95 backdrop-blur-xl sm:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id.toLowerCase()}`}
                                    onClick={() => go(l.id)}
                                    className="rounded-xl px-4 py-3 text-left font-display text-2xl font-semibold text-slate-200 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
